// PlaybackServices.js
// Service for handling image display and comparison mode functionality

export class PlaybackController {
  constructor(options = {}) {
    this.isPlaying = false;
    this.currentIndex = 0;
    this.loop = true;
    this.playbackInterval = null;
    this.fps = options.fps || 1;
    this.playDirection = "forward";
    this.sortOrder = "desc"; // Track the current sort order
  }

  getNextIndex(images, rangeStartDate, rangeEndDate) {
    if (!images.length) return 0;

    const startTime = rangeStartDate.getTime();
    const endTime = rangeEndDate.getTime();

    // Get current range images
    const rangeImages = images.filter((img) => {
      const timestamp = img.userTimestamp || img.timestamp;
      return timestamp >= startTime && timestamp <= endTime;
    });

    if (!rangeImages.length) return this.currentIndex;

    // Find current position in range
    const currentRangeIndex = rangeImages.findIndex((img) => {
      const imgTimestamp = img.userTimestamp || img.timestamp;
      const currentImage = images[this.currentIndex];
      const currentTimestamp =
        currentImage.userTimestamp || currentImage.timestamp;
      return imgTimestamp === currentTimestamp;
    });

    // Adjust playback direction based on sort order to ensure natural timelapse progression
    const effectiveDirection =
      this.sortOrder === "desc"
        ? this.playDirection === "forward"
          ? "backward"
          : "forward"
        : this.playDirection;

    if (effectiveDirection === "forward") {
      if (
        currentRangeIndex === -1 ||
        currentRangeIndex === rangeImages.length - 1
      ) {
        // If at end or outside range, go to first image in range
        const firstRangeImage = rangeImages[0];
        return images.findIndex(
          (img) =>
            (img.userTimestamp || img.timestamp) ===
            (firstRangeImage.userTimestamp || firstRangeImage.timestamp)
        );
      } else {
        // Go to next image in range
        const nextRangeImage = rangeImages[currentRangeIndex + 1];
        return images.findIndex(
          (img) =>
            (img.userTimestamp || img.timestamp) ===
            (nextRangeImage.userTimestamp || nextRangeImage.timestamp)
        );
      }
    } else {
      if (currentRangeIndex === -1 || currentRangeIndex === 0) {
        // If at start or outside range, go to last image in range
        const lastRangeImage = rangeImages[rangeImages.length - 1];
        return images.findIndex(
          (img) =>
            (img.userTimestamp || img.timestamp) ===
            (lastRangeImage.userTimestamp || lastRangeImage.timestamp)
        );
      } else {
        // Go to previous image in range
        const prevRangeImage = rangeImages[currentRangeIndex - 1];
        return images.findIndex(
          (img) =>
            (img.userTimestamp || img.timestamp) ===
            (prevRangeImage.userTimestamp || prevRangeImage.timestamp)
        );
      }
    }
  }

  start(images, onTick, rangeStartDate, rangeEndDate) {
    if (!images.length) return;

    this.isPlaying = true;
    this.playbackInterval = setInterval(() => {
      this.currentIndex = this.getNextIndex(
        images,
        rangeStartDate,
        rangeEndDate
      );
      onTick(this.currentIndex);

      // Only stop if we're at the end/start and loop is disabled
      if (!this.loop) {
        const timestamp =
          images[this.currentIndex].userTimestamp ||
          images[this.currentIndex].timestamp;
        if (
          (this.playDirection === "forward" &&
            timestamp >= rangeEndDate.getTime()) ||
          (this.playDirection === "backward" &&
            timestamp <= rangeStartDate.getTime())
        ) {
          this.stop();
        }
      }
    }, 1000 / this.fps);
  }

  rewind(images, rangeStartDate, rangeEndDate) {
    if (!images.length) return 0;

    const startTime = rangeStartDate.getTime();
    const endTime = rangeEndDate.getTime();
    const currentTimestamp =
      images[this.currentIndex].userTimestamp ||
      images[this.currentIndex].timestamp;

    // Find the previous image within the range
    for (let i = this.currentIndex - 1; i >= 0; i--) {
      const timestamp = images[i].userTimestamp || images[i].timestamp;
      if (timestamp >= startTime && timestamp <= endTime) {
        return i;
      }
    }

    // If no previous image found in range or at start, loop to end
    for (let i = images.length - 1; i > this.currentIndex; i--) {
      const timestamp = images[i].userTimestamp || images[i].timestamp;
      if (timestamp >= startTime && timestamp <= endTime) {
        return i;
      }
    }

    return this.currentIndex;
  }

  fastForward(images, rangeStartDate, rangeEndDate) {
    if (!images.length) return 0;

    const startTime = rangeStartDate.getTime();
    const endTime = rangeEndDate.getTime();
    const currentTimestamp =
      images[this.currentIndex].userTimestamp ||
      images[this.currentIndex].timestamp;

    // Find the next image within the range
    for (let i = this.currentIndex + 1; i < images.length; i++) {
      const timestamp = images[i].userTimestamp || images[i].timestamp;
      if (timestamp >= startTime && timestamp <= endTime) {
        return i;
      }
    }

    // If no next image found in range or at end, loop to start
    for (let i = 0; i < this.currentIndex; i++) {
      const timestamp = images[i].userTimestamp || images[i].timestamp;
      if (timestamp >= startTime && timestamp <= endTime) {
        return i;
      }
    }

    return this.currentIndex;
  }

  togglePlay(images, onTick, rangeStartDate, rangeEndDate) {
    if (this.isPlaying) {
      this.stop();
    } else {
      this.start(images, onTick, rangeStartDate, rangeEndDate);
    }
    return this.isPlaying;
  }

  stop() {
    if (this.playbackInterval) {
      clearInterval(this.playbackInterval);
      this.playbackInterval = null;
    }
    this.isPlaying = false;
  }

  toggleLoop() {
    this.loop = !this.loop;
    return this.loop;
  }

  setPlayDirection(direction) {
    this.playDirection = direction;
  }

  setFPS(fps) {
    this.fps = fps;
    if (this.isPlaying && this.playbackInterval) {
      const wasPlaying = this.isPlaying;
      this.stop();
      if (wasPlaying) {
        this.start();
      }
    }
  }

  setSortOrder(order) {
    this.sortOrder = order;
  }
}

export class DisplayStateManager {
  constructor() {
    this.activeImageMenu = null;
    this.sortOrder = "desc";
  }

  toggleSortOrder() {
    this.sortOrder = this.sortOrder === "desc" ? "asc" : "desc";
    return this.sortOrder;
  }

  getSortOrder() {
    return this.sortOrder;
  }

  toggleImageMenu(index) {
    this.activeImageMenu = this.activeImageMenu === index ? null : index;
    return this.activeImageMenu;
  }

  getActiveImageMenu() {
    return this.activeImageMenu;
  }

  clearActiveImageMenu() {
    this.activeImageMenu = null;
  }
}

export class ComparisonController {
  constructor() {
    this.isEnabled = false;
    this.value = 50;
    this.isDragging = false;
    this.dragStartX = 0;
    this.initialValue = 50;
  }

  toggle() {
    this.isEnabled = !this.isEnabled;
    return this.isEnabled;
  }

  startDrag(event) {
    this.isDragging = true;
    this.dragStartX = event.clientX;
    this.initialValue = this.value;
  }

  handleDrag(event, containerElement) {
    if (!this.isDragging) return this.value;

    const rect = containerElement.getBoundingClientRect();
    const deltaX = event.clientX - this.dragStartX;
    const deltaPercent = (deltaX / rect.width) * 100;

    // Update value with constraints
    this.value = Math.max(0, Math.min(100, this.initialValue + deltaPercent));
    return this.value;
  }

  stopDrag() {
    this.isDragging = false;
  }

  getClipStyle() {
    return {
      clipPath: `inset(0 ${100 - this.value}% 0 0)`,
    };
  }

  getLineStyle() {
    return {
      left: `${this.value}%`,
      transform: "translateX(-50%)",
    };
  }

  handleComparisonSliderInput(event, currentValue) {
    // Add slider input handling logic
    const container = event.target.closest("#comparison");
    if (!container) return currentValue;

    const rect = container.getBoundingClientRect();
    const x = event.clientX - rect.left;
    return Math.max(0, Math.min(100, (x / rect.width) * 100));
  }

  setupDragListeners(startDragFn, handleDragFn, stopDragFn) {
    document.addEventListener("mousemove", handleDragFn);
    document.addEventListener("mouseup", stopDragFn);
  }

  removeDragListeners(handleDragFn, stopDragFn) {
    document.removeEventListener("mousemove", handleDragFn);
    document.removeEventListener("mouseup", stopDragFn);
  }

  shouldMoveToSecondImage(currentIndex, imagesLength) {
    return !this.isEnabled && currentIndex === 0 && imagesLength > 1;
  }

  shouldDisableComparison(currentIndex) {
    return currentIndex === 0;
  }

  handleComparisonModeChange(currentIndex, imagesLength) {
    let newIndex = currentIndex;
    if (this.shouldMoveToSecondImage(currentIndex, imagesLength)) {
      newIndex = 1;
    }
    return {
      newIndex,
      shouldDisable: this.shouldDisableComparison(newIndex),
    };
  }
}

export const setupImageDisplay = (currentImage, loading, images) => {
  if (!currentImage || loading || !images.length) {
    return {
      showLoading: loading,
      showNoImages: !loading && (!images || images.length === 0),
      showImage: !loading && currentImage,
    };
  }

  return {
    showLoading: false,
    showNoImages: false,
    showImage: true,
  };
};

export const formatDate = (timestamp) => {
  if (!timestamp || isNaN(timestamp)) {
    return "No date";
  }
  try {
    const date = new Date(timestamp);
    // Format: YYYY-MM-DD (using UTC to avoid timezone issues)
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  } catch (error) {
    return "Invalid date";
  }
};

// Image Display Management
export class ImageDisplayManager {
  static validateImage(image) {
    return image && image.url && (image.timestamp || image.userTimestamp);
  }

  static getImageDisplayState(currentImage, loading, images) {
    return {
      showLoading: loading,
      showNoImages: !loading && (!images || images.length === 0),
      showImage: !loading && this.validateImage(currentImage),
      hasError:
        !loading && !this.validateImage(currentImage) && images?.length > 0,
    };
  }

  static getSortedImages(images, sortOrder = "desc") {
    if (!Array.isArray(images)) return [];
    return [...images].sort((a, b) => {
      const multiplier = sortOrder === "desc" ? -1 : 1;
      // Use userTimestamp for sorting if available, fallback to system timestamp
      const timeA = a.userTimestamp || a.timestamp;
      const timeB = b.userTimestamp || b.timestamp;
      return multiplier * (timeA - timeB);
    });
  }

  static handleImageSelection(image, images, currentIndex, comparisonMode) {
    const newIndex = images.findIndex((img) => img.id === image.id);
    if (newIndex === -1)
      return { index: currentIndex, shouldDisableComparison: false };

    let shouldDisableComparison = false;
    let index = newIndex;

    if (comparisonMode) {
      if (newIndex === 0 && images.length > 1) {
        index = 1;
      } else if (newIndex === 0) {
        shouldDisableComparison = true;
      }
    }

    return { index, shouldDisableComparison };
  }

  static getImageTimestamp(image) {
    if (!image) return "No date";
    // Use userTimestamp for display if available, fallback to system timestamp
    return formatDate(image.userTimestamp || image.timestamp);
  }

  static getImageAltText(image, index, total) {
    if (!image) return "";
    return `Image ${index + 1} of ${total}`;
  }

  static getImageDisplayClass(image, currentImage) {
    if (!image || !currentImage) return "";
    return currentImage.id === image.id
      ? "bg-neutral-950 ring-1 ring-[#27bdf4]"
      : "";
  }
}

export class TimelineService {
  constructor() {
    this.isDragging = false;
    this.activeDragHandle = null;
    this.dragStartX = 0;
    this.dragStartValue = 0;
    this.rangeStart = 0;
    this.rangeEnd = 100;
    this.isDraggingImage = false;
    this.imageIndicatorStartX = 0;
    this.imageIndicatorStartPosition = 0;
    this.lastDragUpdate = 0;
    this.dragThrottleMs = 16; // ~60fps
    this.snapThreshold = 2; // percentage threshold for snapping
    this.minGap = 5; // minimum gap between handles
    this.dragOffset = 0;
  }

  getTimestamp(image) {
    return image.userTimestamp || image.timestamp;
  }

  startDragging(handle, event, currentValue) {
    if (this.isDragging) return;

    this.isDragging = true;
    this.activeDragHandle = handle;
    this.dragStartX = event.clientX;
    this.dragStartValue = currentValue;

    // Calculate and store the offset between click position and handle center
    const handleElement = event.target;
    const handleRect = handleElement.getBoundingClientRect();
    this.dragOffset = event.clientX - (handleRect.left + handleRect.width / 2);
  }

  handleDrag(event, timelineRect, images) {
    if (!this.isDragging || !images.length) return null;

    // Throttle updates for performance
    const now = Date.now();
    if (now - this.lastDragUpdate < this.dragThrottleMs) return null;
    this.lastDragUpdate = now;

    // Calculate position considering the drag offset
    const adjustedClientX = event.clientX - this.dragOffset;
    const relativeX = adjustedClientX - timelineRect.left;
    let newPercentage = (relativeX / timelineRect.width) * 100;

    // Constrain to timeline bounds with smooth clamping
    newPercentage = Math.max(0, Math.min(100, newPercentage));

    // Get timestamps for snapping
    const timestamps = images
      .map((img) => this.getTimestamp(img))
      .sort((a, b) => a - b);
    const oldestTimestamp = timestamps[0];
    const newestTimestamp = timestamps[timestamps.length - 1];
    const timeRange = newestTimestamp - oldestTimestamp;

    // Convert percentage to timestamp
    const targetTimestamp = oldestTimestamp + (timeRange * newPercentage) / 100;

    // Find nearest timestamp with improved snapping logic
    let nearestTimestamp = oldestTimestamp;
    let minDistance = Math.abs(targetTimestamp - oldestTimestamp);

    for (const timestamp of timestamps) {
      const distance = Math.abs(timestamp - targetTimestamp);
      if (distance < minDistance) {
        minDistance = distance;
        nearestTimestamp = timestamp;
      }
    }

    // Convert nearest timestamp back to percentage
    let snappedValue = ((nearestTimestamp - oldestTimestamp) / timeRange) * 100;

    // Get the current other handle's position in timestamp units
    const otherHandlePercentage =
      this.activeDragHandle === "start" ? this.rangeEnd : this.rangeStart;
    const otherHandleTimestamp =
      oldestTimestamp + (timeRange * otherHandlePercentage) / 100;

    // Find the nearest valid timestamp that respects the minimum gap
    if (this.activeDragHandle === "start") {
      // For start handle, find the nearest timestamp that's not too close to end
      const validTimestamps = timestamps.filter((ts) => {
        const tsPercentage = ((ts - oldestTimestamp) / timeRange) * 100;
        return tsPercentage <= otherHandlePercentage - this.minGap;
      });

      if (validTimestamps.length > 0) {
        nearestTimestamp = validTimestamps.reduce((prev, curr) => {
          return Math.abs(curr - targetTimestamp) <
            Math.abs(prev - targetTimestamp)
            ? curr
            : prev;
        });
        snappedValue = ((nearestTimestamp - oldestTimestamp) / timeRange) * 100;
      } else {
        // If no valid timestamps found, use the last valid one
        const lastValidTimestamp = timestamps.find((ts) => {
          const tsPercentage = ((ts - oldestTimestamp) / timeRange) * 100;
          return tsPercentage <= otherHandlePercentage - this.minGap;
        });
        if (lastValidTimestamp) {
          snappedValue =
            ((lastValidTimestamp - oldestTimestamp) / timeRange) * 100;
        }
      }
    } else {
      // For end handle, find the nearest timestamp that's not too close to start
      const validTimestamps = timestamps.filter((ts) => {
        const tsPercentage = ((ts - oldestTimestamp) / timeRange) * 100;
        return tsPercentage >= otherHandlePercentage + this.minGap;
      });

      if (validTimestamps.length > 0) {
        nearestTimestamp = validTimestamps.reduce((prev, curr) => {
          return Math.abs(curr - targetTimestamp) <
            Math.abs(prev - targetTimestamp)
            ? curr
            : prev;
        });
        snappedValue = ((nearestTimestamp - oldestTimestamp) / timeRange) * 100;
      } else {
        // If no valid timestamps found, use the first valid one
        const firstValidTimestamp = timestamps.find((ts) => {
          const tsPercentage = ((ts - oldestTimestamp) / timeRange) * 100;
          return tsPercentage >= otherHandlePercentage + this.minGap;
        });
        if (firstValidTimestamp) {
          snappedValue =
            ((firstValidTimestamp - oldestTimestamp) / timeRange) * 100;
        }
      }
    }

    return snappedValue;
  }

  stopDragging() {
    this.isDragging = false;
    this.activeDragHandle = null;
  }

  startImageDragging(event, currentImagePosition) {
    if (this.isDraggingImage) return;

    this.isDraggingImage = true;
    this.imageIndicatorStartX = event.clientX;
    this.imageIndicatorStartPosition = currentImagePosition;

    // Calculate and store the offset for the image indicator
    const indicatorElement = event.target;
    const indicatorRect = indicatorElement.getBoundingClientRect();
    this.dragOffset =
      event.clientX - (indicatorRect.left + indicatorRect.width / 2);
  }

  handleImageDrag(event, timelineRect, images) {
    if (!this.isDraggingImage || !images.length) return null;

    // Throttle updates
    const now = Date.now();
    if (now - this.lastDragUpdate < this.dragThrottleMs) return null;
    this.lastDragUpdate = now;

    // Calculate position with offset correction
    const adjustedClientX = event.clientX - this.dragOffset;
    const relativeX = adjustedClientX - timelineRect.left;
    let newPercentage = (relativeX / timelineRect.width) * 100;

    // Smooth clamping
    newPercentage = Math.max(0, Math.min(100, newPercentage));

    // Get timestamps for snapping
    const timestamps = images.map((img) => this.getTimestamp(img));
    const oldestTimestamp = Math.min(...timestamps);
    const newestTimestamp = Math.max(...timestamps);
    const timeRange = newestTimestamp - oldestTimestamp;

    // Convert percentage to timestamp
    const targetTimestamp = oldestTimestamp + (timeRange * newPercentage) / 100;

    // Enhanced nearest timestamp finding with momentum
    let nearestTimestamp = oldestTimestamp;
    let minDistance = Math.abs(targetTimestamp - oldestTimestamp);

    for (const timestamp of timestamps) {
      const distance = Math.abs(timestamp - targetTimestamp);
      if (distance < minDistance) {
        minDistance = distance;
        nearestTimestamp = timestamp;
      }
    }

    return nearestTimestamp;
  }

  stopImageDragging() {
    this.isDraggingImage = false;
  }

  handleTimelineClick(event, timelineRect, images) {
    if (!images.length) return null;

    // Calculate exact click position
    const relativeX = event.clientX - timelineRect.left;
    const clickPercentage = (relativeX / timelineRect.width) * 100;

    // Get timestamps
    const timestamps = images.map((img) => this.getTimestamp(img));
    const oldestTimestamp = Math.min(...timestamps);
    const newestTimestamp = Math.max(...timestamps);
    const timeRange = newestTimestamp - oldestTimestamp;

    // Convert percentage to timestamp
    const targetTimestamp =
      oldestTimestamp + (timeRange * clickPercentage) / 100;

    // Find nearest timestamp with improved accuracy
    return timestamps.reduce((prev, curr) => {
      return Math.abs(curr - targetTimestamp) < Math.abs(prev - targetTimestamp)
        ? curr
        : prev;
    });
  }

  getTimeMarkers(images) {
    if (!images.length) return [];

    const timestamps = images.map((img) => this.getTimestamp(img));
    const oldestTimestamp = Math.min(...timestamps);
    const newestTimestamp = Math.max(...timestamps);

    if (newestTimestamp === oldestTimestamp) return [];

    return images.map((image) => ({
      date: formatDate(this.getTimestamp(image)),
      position:
        ((this.getTimestamp(image) - oldestTimestamp) /
          (newestTimestamp - oldestTimestamp)) *
        100,
    }));
  }

  getCurrentImagePosition(currentImage, images) {
    if (!currentImage || !images.length) return 0;

    const timestamps = images.map((img) => this.getTimestamp(img));
    const oldestTimestamp = Math.min(...timestamps);
    const newestTimestamp = Math.max(...timestamps);

    if (newestTimestamp === oldestTimestamp) return 0;

    return Math.max(
      0,
      Math.min(
        100,
        ((this.getTimestamp(currentImage) - oldestTimestamp) /
          (newestTimestamp - oldestTimestamp)) *
          100
      )
    );
  }

  getTimelineStyle(currentImage, images) {
    if (!currentImage || !images.length) return { width: "0%", left: "0%" };

    const timestamps = images.map((img) => this.getTimestamp(img));
    const oldestTimestamp = Math.min(...timestamps);
    const newestTimestamp = Math.max(...timestamps);

    if (newestTimestamp === oldestTimestamp) return { width: "0%", left: "0%" };

    // Get the range start and end timestamps
    const rangeStartTime = this.getRangeStartDate(images).getTime();
    const rangeEndTime = this.getRangeEndDate(images).getTime();
    const currentTime = this.getTimestamp(currentImage);

    // If current image is before range start, no progress
    if (currentTime <= rangeStartTime) {
      return { width: "0%", left: `${this.rangeStart}%` };
    }

    // If current image is after range end, full progress
    if (currentTime >= rangeEndTime) {
      return {
        width: `${this.rangeEnd - this.rangeStart}%`,
        left: `${this.rangeStart}%`,
      };
    }

    // Calculate progress within the selected range
    const rangeProgress =
      ((currentTime - rangeStartTime) / (rangeEndTime - rangeStartTime)) *
      (this.rangeEnd - this.rangeStart);
    return {
      width: `${Math.max(
        0,
        Math.min(this.rangeEnd - this.rangeStart, rangeProgress)
      )}%`,
      left: `${this.rangeStart}%`,
    };
  }

  getRangeStartDate(images) {
    if (!images.length) return new Date();

    const timestamps = images.map((img) => this.getTimestamp(img));
    const oldestTimestamp = Math.min(...timestamps);
    const newestTimestamp = Math.max(...timestamps);

    if (newestTimestamp === oldestTimestamp) return new Date(oldestTimestamp);

    const range = newestTimestamp - oldestTimestamp;
    return new Date(oldestTimestamp + (range * this.rangeStart) / 100);
  }

  getRangeEndDate(images) {
    if (!images.length) return new Date();

    const timestamps = images.map((img) => this.getTimestamp(img));
    const oldestTimestamp = Math.min(...timestamps);
    const newestTimestamp = Math.max(...timestamps);

    if (newestTimestamp === oldestTimestamp) return new Date(newestTimestamp);

    const range = newestTimestamp - oldestTimestamp;
    return new Date(oldestTimestamp + (range * this.rangeEnd) / 100);
  }

  getCurrentRangeImages(images) {
    if (!images.length) return [];

    const startTime = this.getRangeStartDate(images)?.getTime();
    const endTime = this.getRangeEndDate(images)?.getTime();

    if (!startTime || !endTime) return [];

    return images.filter((img) => {
      const timestamp = this.getTimestamp(img);
      return (
        timestamp &&
        !isNaN(timestamp) &&
        timestamp >= startTime &&
        timestamp <= endTime
      );
    });
  }
}
